import axios from 'axios';

// Create axios instance with base URL from environment variables
const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const VITE_FRONTEND_URL = import.meta.env.VITE_FRONTEND_URL;

// Canonical backend base URL (all routes are under /api/v1)
// IMPORTANT: if your deployment env vars are mis-typed (e.g. "swarnasetu" vs "swarnsetu"),
// we auto-correct a few common issues here to avoid silent production breakages.
const DEFAULT_API_BASE_URL = 'https://n-mgxgoldbackend-434274078606.asia-south1.run.app/api/v1';

const normalizeApiBaseUrl = (raw) => {
  if (!raw || typeof raw !== 'string') return null;

  let v = raw.trim();
  if (!v) return null;

  // Allow providing just a host (e.g. panel.swarnsetu.in)
  if (!/^https?:\/\//i.test(v)) {
    v = `https://${v}`;
  }

  // Auto-fix common typo seen in deployments
  v = v.replace(/swarnasetu/gi, 'swarnsetu');

  // Remove trailing slashes
  v = v.replace(/\/+$/, '');

  try {
    const u = new URL(v);

    // Normalize to /api/v1
    if (/\/api\/v1$/i.test(u.pathname)) return u.toString();

    if (/\/api$/i.test(u.pathname)) {
      u.pathname = u.pathname.replace(/\/api$/i, '/api/v1');
      return u.toString();
    }

    if (!u.pathname || u.pathname === '/') {
      u.pathname = '/api/v1';
      return u.toString();
    }

    // If a longer path is provided, append /api/v1 (we don't support api versions other than v1 here)
    u.pathname = u.pathname.replace(/\/+$/, '') + '/api/v1';
    return u.toString();
  } catch {
    return null;
  }
};

// Ensure we always have a valid API base URL as per guide specifications
const API_BASE_URL = normalizeApiBaseUrl(VITE_API_BASE_URL) || DEFAULT_API_BASE_URL;
const API_ORIGIN = (() => {
  try {
    return new URL(API_BASE_URL).origin;
  } catch {
    return API_BASE_URL.replace(/\/api\/v1$/, '');
  }
})();

export { API_ORIGIN, API_BASE_URL, DEFAULT_API_BASE_URL, normalizeApiBaseUrl };

// Validate axios import to prevent minification issues
if (!axios || typeof axios !== 'function') {
  throw new Error('Axios library not properly loaded');
}


const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 45000, // Increased to 45 seconds to handle slow Render.com cold starts
  // Do NOT send cookies cross-origin; we use Authorization Bearer tokens instead.
  // This avoids requiring Access-Control-Allow-Credentials on the server and fixes CORS errors on public endpoints.
  withCredentials: false,
});

// Global request throttling to avoid 429s from the host
let activeRequests = 0;
const MAX_CONCURRENT = 2; // stricter parallel limit to avoid upstream 429
let waitQueue = [];
let globalBackoffUntil = 0; // epoch ms until which we delay new requests
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Simple in-flight de-dup and short-term cache for GETs
const inFlight = new Map(); // key -> Promise<AxiosResponse>
const cache = new Map(); // key -> { data, expires }
const now = () => Date.now();
const keyFor = (config) => {
  const method = (config.method || 'get').toUpperCase();
  const url = (config.baseURL || '') + (config.url || '');
  const params = config.params ? JSON.stringify(config.params) : '';
  return `${method} ${url} ${params}`;
};
const ttlFor = (path) => {
  if (/\/gold-rates\b/.test(path)) return 10000; // 10s
  if (/\/plans\b/.test(path)) return 60000; // 60s
  if (/\/(bank-details|kyc|notifications|transactions)\b/.test(path)) return 7000; // 7s
  if (/\/dashboard\//.test(path)) return 5000; // 5s
  return 3000; // default 3s
};

// Request interceptor to add auth token and throttle
apiClient.interceptors.request.use(
  async (config) => {
    // Respect global backoff if a recent 429 was received
    while (Date.now() < globalBackoffUntil) {
      await sleep(250);
    }

    // Limit concurrency
    if (activeRequests >= MAX_CONCURRENT) {
      await new Promise((resolve) => waitQueue.push(resolve));
    }
    activeRequests++;

    // Check for auth token in storage (avoid attaching to public endpoints to prevent CORS preflights)
    const token =
      localStorage.getItem('auth_token') ||
      localStorage.getItem('mgx_token') ||
      sessionStorage.getItem('auth_token') ||
      sessionStorage.getItem('mgx_token');

    const urlPath = (config.url || '').toString();
    const method = (config.method || 'get').toUpperCase();

    // Define public endpoints (GET only for gold-rates and branding)
    const isPublic = [
      /^\/auth\/(login|register|verify-otp|send-otp|send-default-otp-admin|forgot-password|reset-password|refresh-token|validate-credentials)\b/i,
    ].some((rx) => rx.test(urlPath)) ||
      (method === 'GET' && (/^\/gold-rates\b/i.test(urlPath) || /^\/settings\/branding\b/i.test(urlPath)));

    console.log('🔍 [Axios] Request interceptor:', {
      url: urlPath,
      method,
      hasToken: !!token,
      isPublic,
      tokenLength: token?.length || 0
    });

    if (token && !isPublic) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
      console.log('✅ [Axios] Authorization header attached');
    } else if (!token && !isPublic) {
      console.warn('⚠️ [Axios] No token found for protected endpoint!');
    }

    // Handle trailing slashes in URLs - Remove trailing slashes
    if (config.url && config.url.length > 1) {
      while (config.url.endsWith('/')) {
        config.url = config.url.slice(0, -1);
      }
    }

    // Ensure proper headers: only set Content-Type for requests with a body (avoids unnecessary CORS preflights on GET)
    const isFormData = (typeof FormData !== 'undefined') && (config.data instanceof FormData);
    const hasBody = ['POST', 'PUT', 'PATCH'].includes(method);

    // Ensure headers object exists before mutation
    config.headers = config.headers || {};

    if (isFormData) {
      // Let browser set the correct multipart boundary
      if (config.headers && config.headers['Content-Type']) {
        delete config.headers['Content-Type'];
      }
    } else if (hasBody) {
      config.headers['Content-Type'] = 'application/json';
    } else {
      // Remove Content-Type for GET/HEAD/DELETE to keep requests simple and avoid preflight
      if (config.headers && config.headers['Content-Type']) {
        delete config.headers['Content-Type'];
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors as per guide specifications

apiClient.interceptors.response.use(
  (response) => {
    // Release concurrency slot
    activeRequests = Math.max(0, activeRequests - 1);
    const next = waitQueue.shift();
    if (next) next();

    return response.data;
  },
  async (error) => {
    // Release concurrency slot on error
    activeRequests = Math.max(0, activeRequests - 1);
    const next = waitQueue.shift();
    if (next) next();

    // Handle network errors (including CORS and timeouts)
    if (!error.response) {
      console.error('Network error:', error.message);
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        return Promise.reject(new Error('Request timeout. The server is taking too long to respond. Please try again.'));
      }
      if (error.message.includes('CORS') || error.message.includes('Access-Control-Allow-Origin')) {
        return Promise.reject(new Error('CORS error: Backend does not allow requests from this origin. Please contact support.'));
      }
      return Promise.reject(new Error('Network error. Please check your connection.'));
    }

    const { status, data } = error.response;

    // Handle specific status codes as per guide
    switch (status) {
      case 0:
        // CORS or network error
        return Promise.reject(new Error('CORS error: Unable to connect to the server. Please check your internet connection.'));

      case 400: {
        const message = data?.error || data?.message || 'Bad Request';
        const err = new Error(message);
        err.status = 400;
        err.data = data;
        return Promise.reject(err);
      }

      case 401:
        // Token expired or invalid, try to refresh it once
        const requestUrl = error.config?.url || '';
        const requestMethod = (error.config?.method || 'get').toUpperCase();
        const hasPublicFallback = requestUrl.includes('/plans/all'); // Admin-only endpoint with public fallback

        // Don't retry POST to gold-rates (admin-only) or if already retried
        const isGoldRatePost = requestMethod === 'POST' && requestUrl.includes('/gold-rates');
        const alreadyRetried = error.config?._retry401;

        if (!isGoldRatePost && !alreadyRetried) {
          try {
            const rt = localStorage.getItem('refresh_token');
            if (rt) {
              error.config._retry401 = true; // Mark as retried
              const refreshResponse = await apiClient.post('/auth/refresh-token', { refreshToken: rt });
              if (refreshResponse?.accessToken) {
                localStorage.setItem('auth_token', refreshResponse.accessToken);
                sessionStorage.setItem('auth_token', refreshResponse.accessToken);
                localStorage.setItem('mgx_token', refreshResponse.accessToken);
                sessionStorage.setItem('mgx_token', refreshResponse.accessToken);
                if (refreshResponse.refreshToken) {
                  localStorage.setItem('refresh_token', refreshResponse.refreshToken);
                }
                const originalRequest = error.config;
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${refreshResponse.accessToken}`;
                return apiClient(originalRequest);
              }
            }
          } catch (refreshError) {
            // Only log refresh errors if not an expected auth failure with fallback
            if (!hasPublicFallback && !isGoldRatePost) {
              console.error('Token refresh failed:', refreshError);
            }
          }
        }
        // Do not hard redirect here; propagate 401 so caller can decide (prevents instant logout loops)
        const authErr = new Error('Authentication required. Please login again.');
        authErr.status = 401;
        authErr.response = error.response; // Preserve response for better error handling
        return Promise.reject(authErr);

      case 403: {
        const err = new Error('Access denied. Insufficient permissions.');
        err.status = 403;
        return Promise.reject(err);
      }

      case 404: {
        const err = new Error('Resource not found.');
        err.status = 404;
        return Promise.reject(err);
      }

      case 429:
        // Enhanced rate limiting handling with global backoff and single automatic retry
        try {
          const retryAfterHeader = error.response.headers['retry-after'];
          const baseDelay = retryAfterHeader ? parseInt(retryAfterHeader, 10) * 1000 : 5000; // Default to 5 seconds
          const jitter = Math.floor(200 + Math.random() * 600);
          const delay = baseDelay + jitter;
          const originalRequest = error.config || {};
          // Set global backoff so other requests wait
          globalBackoffUntil = Date.now() + delay;
          if (!originalRequest._retry429) {
            originalRequest._retry429 = true;
            console.warn(`Rate limit exceeded. Retrying once after ${delay}ms`);
            return new Promise((resolve) => {
              setTimeout(() => {
                resolve(apiClient(originalRequest));
              }, delay);
            });
          }
        } catch (e) {
          // fall through to rejection below
        }
        return Promise.reject(new Error('Too many requests. Please wait a few seconds before trying again.'));

      case 409:
        return Promise.reject(new Error(data?.message || 'Conflict: resource already exists.'));

      case 500:
        return Promise.reject(new Error('Server error. Please try again later.'));

      case 502:
        return Promise.reject(new Error('Server temporarily unavailable. Please try again later.'));

      case 503:
        return Promise.reject(new Error('Service unavailable. Please try again later.'));

      case 504:
        return Promise.reject(new Error('Gateway timeout. The server is taking too long to respond. Please try again later.'));

      default:
        const message = data?.message || `Request failed: ${status} ${error.response.statusText}`;
        const err = new Error(message);
        err.status = status;
        err.data = data;
        return Promise.reject(err);
    }
  }
);

// Add a method to set auth token
apiClient.setAuthToken = (token, refreshToken = null) => {
  if (token) {
    localStorage.setItem('auth_token', token);
  } else {
    localStorage.removeItem('auth_token');
  }

  if (refreshToken) {
    localStorage.setItem('refresh_token', refreshToken);
  } else {
    localStorage.removeItem('refresh_token');
  }
};

// Add a method to get auth token
apiClient.getAuthToken = () => {
  return localStorage.getItem('auth_token');
};

// Add a method to clear auth data
apiClient.clearAuth = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('mgx_user');
  localStorage.removeItem('mgx_user_type');
};

export default apiClient;